import React, { useEffect, useRef, useState } from "react";

import { isNullOrUndefined, useGetFootprintingBeefHerdDetails, useMutateFootprintingBeefHerdDetails, useTranslation } from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

type BeefHerdOffspringFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const BeefHerdOffspringForm = ({ primaryDataId, readOnly, farmId, year }: BeefHerdOffspringFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [fatteningStartWeightKg, setFatteningStartWeightKg] = useState<number | null>(null);
  const [calfMilkFeedingPeriodDays, setCalfMilkFeedingPeriodDays] = useState<number | null>(null);
  const [rearingTimeDays, setRearingTimeDays] = useState<number | null>(null);

  const { data: herdDetails, isSuccess, isLoading } = useGetFootprintingBeefHerdDetails(primaryDataId ?? "", { enabled: !!primaryDataId });

  const { mutate: mutateHerdDetails } = useMutateFootprintingBeefHerdDetails(primaryDataId ?? "", farmId, year, {
    fatteningStartWeightKg,
    calfMilkFeedingPeriodDays,
    rearingTimeDays,
  });

  useEffect(() => {
    if (herdDetails && isSuccess) {
      setFatteningStartWeightKg(herdDetails.fatteningStartWeightKg ?? null);
      setCalfMilkFeedingPeriodDays(herdDetails.calfMilkFeedingPeriodDays ?? null);
      setRearingTimeDays(herdDetails.rearingTimeDays ?? null);

      formRef.current?.setFieldsValue({
        fatteningStartWeightKg: herdDetails.fatteningStartWeightKg ?? null,
        calfMilkFeedingPeriodDays: herdDetails.calfMilkFeedingPeriodDays ?? null,
        rearingTimeDays: herdDetails.rearingTimeDays ?? null,
      });

      const fieldsToValidate = (["fatteningStartWeightKg", "calfMilkFeedingPeriodDays", "rearingTimeDays"] as const).some(
        (field) => herdDetails[field] !== null && herdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [herdDetails, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateHerdDetails();
      }
    },
    [isLoading, mutateHerdDetails, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-beef-herd-offspring-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Typography variant="h3">{t("beefHerdOffspringForm.question.calvesFeedTitle")}</Typography>

          <InputNumber
            className="floating-label"
            name="fatteningStartWeightKg"
            disabled={readOnly}
            dataTestId="footprinting-beef-fattening-start-weight-input"
            label={t("beefHerdOffspringForm.question.fatteningStartWeightInput")}
            suffix="kg"
            value={fatteningStartWeightKg}
            initialValue={fatteningStartWeightKg}
            onChange={(v) => setFatteningStartWeightKg(v)}
            min={0}
            validateFirst
            rules={[
              { required: true, message: t("beefHerdOffspringForm.error.validWeight") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || v < 0) {
                    return Promise.reject(new Error(t("beefHerdOffspringForm.error.validWeight")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
            {t("beefHerdOffspringForm.question.fatteningStartWeightDescription")}
          </Typography>

          <InputNumber
            className="floating-label"
            name="calfMilkFeedingPeriodDays"
            disabled={readOnly}
            dataTestId="footprinting-beef-calf-milk-feeding-period-days-input"
            label={t("beefHerdOffspringForm.question.calfMilkFeedingPeriodDaysInput")}
            suffix="days"
            value={calfMilkFeedingPeriodDays}
            initialValue={calfMilkFeedingPeriodDays}
            onChange={(v) => setCalfMilkFeedingPeriodDays(v)}
            min={0}
            validateFirst
            rules={[
              { required: true, message: t("beefHerdOffspringForm.error.validMilkFeedingPeriod") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !Number.isInteger(v) || v < 0) {
                    return Promise.reject(new Error(t("beefHerdOffspringForm.error.validMilkFeedingPeriod")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
            {t("beefHerdOffspringForm.question.calfMilkFeedingPeriodDaysDescription")}
          </Typography>

          {/* TODO: rearingTimeDays is not yet wired into any PEAS/LFL calculation — aufzuchtTage (the LFL "rearing days" parameter) is already
              used by calfMilkFeedingPeriodDays. Needs follow-up to find/confirm this field's actual use in the beef footprint calculation. */}
          <InputNumber
            className="floating-label"
            name="rearingTimeDays"
            disabled={readOnly}
            dataTestId="footprinting-beef-rearing-time-days-input"
            label={t("beefHerdOffspringForm.question.rearingTimeDaysInput")}
            suffix="days"
            value={rearingTimeDays}
            initialValue={rearingTimeDays}
            onChange={(v) => setRearingTimeDays(v)}
            min={0}
            validateFirst
            rules={[
              { required: true, message: t("beefHerdOffspringForm.error.validRearingTimeDays") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !Number.isInteger(v) || v < 0) {
                    return Promise.reject(new Error(t("beefHerdOffspringForm.error.validRearingTimeDays")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
            {t("beefHerdOffspringForm.question.rearingTimeDaysDescription")}
          </Typography>
        </Flex>
      </Form>
    </Card>
  );
};

BeefHerdOffspringForm.Key = "beef.herdOffspring";
export { BeefHerdOffspringForm };
